import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { bapi, brandLabel, fmtDate, usd } from '@/lib/billingApi';
import { StatusPill } from '@/components/billing/StatusPill';
import { GatewayTag, PRODUCT_SHORT, Sel, TestTag, Th, statusText } from './shared';

const VIEWS = [['all', 'All subscriptions'], ['upcoming', 'Renewing in 30 days'], ['failed', 'Failed / action needed']];

const Row = ({ s }) => (
  <tr className="border-b border-slate-100" data-testid={`sub-row-${s.id}`}>
    <td className="px-3 py-2.5 font-semibold text-slate-800 max-w-[240px] truncate">{s.user_email}</td>
    <td className="px-3 py-2.5 whitespace-nowrap">{PRODUCT_SHORT[s.product_id] || s.product_name}</td>
    <td className="px-3 py-2.5 whitespace-nowrap"><GatewayTag gateway={s.gateway} />{s.card_last4 && <span className="text-xs text-slate-500 ml-1">{brandLabel(s.card_brand)} •••• {s.card_last4}</span>}</td>
    <td className="px-3 py-2.5 whitespace-nowrap">
      <StatusPill status={s.status} label={statusText(s.status)} />{s.simulated && <TestTag />}
      {!s.auto_renew && s.status === 'active' && <span className="block text-[11px] text-slate-500 mt-0.5">Auto-renew off</span>}
      {s.action_required && <span className="block text-[11px] text-amber-700 mt-0.5">Card confirmation needed</span>}
    </td>
    <td className="px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap">{s.amount ? usd(s.amount) : '—'}</td>
    <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{fmtDate(s.current_period_end)}</td>
    <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{s.auto_renew && s.next_charge_at ? fmtDate(s.next_charge_at) : '—'}</td>
    <td className="px-3 py-2.5 text-xs text-red-600 max-w-[200px]">{s.retry?.last_error ? `${s.retry.count || 1}× ${s.retry.last_error}` : ''}</td>
  </tr>
);

export const SubscriptionsTable = ({ refreshKey }) => {
  const [view, setView] = useState('all');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      bapi('/admin/billing/subscriptions', { query: { view, q } }).then((d) => setRows(d.rows)).catch((e) => toast.error(e.message));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [view, q, refreshKey]);

  return (
    <Card className="border-slate-200">
      <CardHeader className="space-y-4">
        <CardTitle className="text-lg flex items-center gap-2"><RefreshCw className="w-5 h-5 text-[#00247D]" />Subscriptions ({rows?.length ?? 0})</CardTitle>
        <div className="flex flex-wrap gap-3">
          <Sel value={view} onChange={setView} options={VIEWS} testId="subs-view-filter" />
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer email" className="pl-8" data-testid="subs-search" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {!rows ? <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00247D]" /></div>
          : !rows.length ? <p className="p-8 text-center text-slate-500 text-sm" data-testid="subs-empty">No subscriptions in this view.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-y border-slate-200"><tr>
                  <Th>Customer</Th><Th>Product</Th><Th>Method</Th><Th>Status</Th><Th right>Amount</Th><Th>Period ends</Th><Th>Next charge</Th><Th>Retries</Th>
                </tr></thead>
                <tbody data-testid="subs-rows">{rows.map((s) => <Row key={s.id} s={s} />)}</tbody>
              </table>
            </div>
          )}
      </CardContent>
    </Card>
  );
};
